/**
 * What the signed-in person may do (docs/rbac.md section 8): `GET /me/v1/permissions`, read once per sign-in and
 * organisation and shared by every view. The console hides what the person cannot do; the API still decides every
 * request, so a page that shows something by mistake is refused there.
 *
 * `mode` is the deployment's: `today` while roles and permissions are off, when the menu shows every area as it always
 * did, and `roles` once they are on, when the menu shows only the areas the person may View.
 */

import { grantsAllow, type AreaKey, type MyPermissions, type RoleScope, type Verb } from '@aioe/shared';
import { currentOrganisation, rbacRoutes } from './api/client.ts';
import { useLoad } from './hooks.ts';

/** How long one answer is used before it is asked again (roles change rarely; a refusal corrects the rest). */
const FRESH_MS = 5 * 60_000;

let cached: { organisation: string | undefined; at: number; answer: Promise<MyPermissions | undefined> } | undefined;

/** The caller's permissions, one request for every view that asks at once. Undefined when they cannot be read. */
export function myPermissions(): Promise<MyPermissions | undefined> {
  const organisation = currentOrganisation();
  if (!cached || cached.organisation !== organisation || Date.now() - cached.at > FRESH_MS) {
    const answer = rbacRoutes.mine().catch(() => undefined);
    cached = { organisation, at: Date.now(), answer };
    // A failed read is not kept: the next view asks again.
    void answer.then((a) => {
      if (!a && cached?.answer === answer) cached = undefined;
    });
  }
  return cached.answer;
}

/** Forget the answer (after a sign-in or a role change on the Roles and permissions screen). */
export function forgetPermissions(): void {
  cached = undefined;
}

/** Whether `p` holds `verb` on `area` across the organisation; `own` counts a verb held only on the person's own things. */
export function holds(p: MyPermissions | undefined, area: AreaKey, verb: Verb, opts: { own?: boolean } = {}): boolean {
  const a = p?.organisation[area];
  if (!a) return false;
  return a.all.includes(verb) || (!!opts.own && a.own.includes(verb));
}

/**
 * The areas that mean something inside one team or project: the work a team's administrator or a project's member
 * does. A role held at a team holds every area on paper, but the platform's own views (nodes, updates, mesh, platform
 * access, settings) stay the organisation's, so a scoped role never opens those menu entries.
 */
export const SCOPED_AREAS: ReadonlySet<AreaKey> = new Set<AreaKey>([
  'agents.specialists',
  'agents.activity',
  'agents.live',
  'teams.teams',
  'projects.projects',
  'projects.register',
  'workflows.apps',
  'workflows.routines',
  'workflows.workflows',
  'workflows.event-sources',
  'economics.budgets',
  'audit.audit',
]);

/** A team or a project the person holds a role in. */
export type HeldScope = Extract<RoleScope, { type: 'team' | 'project' }>;

/**
 * The teams or projects where the person holds `verb` on `area` through a role there, from what
 * `GET /me/v1/permissions` says (`held` names each role and where; `roles` its grants). `assigned` leaves out a team the
 * person only owns: the owner of a team views its budgets, an assigned Team Administrator changes them
 * (docs/rbac.md, before switching on, item 2).
 */
export function scopesHolding(p: MyPermissions | undefined, area: AreaKey, verb: Verb, opts: { type?: HeldScope['type']; assigned?: boolean } = {}): HeldScope[] {
  const out = new Map<string, HeldScope>();
  for (const h of p?.held ?? []) {
    const s = h.scope;
    if (s.type === 'organisation' || (opts.type && s.type !== opts.type)) continue;
    if (opts.assigned && h.source === 'team-owner') continue;
    if (!grantsAllow(p?.roles[h.roleId] ?? [], area, verb)) continue;
    out.set(`${s.type}:${s.id}`, s);
  }
  return [...out.values()];
}

/** Whether `p` may see `area` at all: View on it, on everything or on their own, or in a team or a project they hold a role in. */
export function sees(p: MyPermissions | undefined, area: AreaKey): boolean {
  if (holds(p, area, 'view', { own: true })) return true;
  return SCOPED_AREAS.has(area) && scopesHolding(p, area, 'view').length > 0;
}

/** Whether `verb` on `area` is held across the organisation, or in some team or project (the API still decides each one). */
export function holdsAnywhere(p: MyPermissions | undefined, area: AreaKey, verb: Verb): boolean {
  return holds(p, area, verb) || scopesHolding(p, area, verb).length > 0;
}

/** The permissions in a component: `data` undefined while loading or when they cannot be read. */
export function usePermissions() {
  return useLoad(() => myPermissions(), [currentOrganisation()]);
}

/**
 * Whether the person may do `verb` on `area` across the organisation: undefined while the answer loads, false when it
 * cannot be read (as a refused probe read before).
 */
export function useMay(area: AreaKey, verb: Verb): boolean | undefined {
  const p = usePermissions();
  return p.loading && p.data === undefined ? undefined : holds(p.data, area, verb);
}
